import { put, takeEvery, select, call, type SagaReturnType } from 'redux-saga/effects'
import {
    getScooterHistoryTasks,
    updateVehicle,
    getVehicle,
    removeFromRepair as removeFromRepairRequest,
    bikeUnarmLock as bikeUnarmLockRequest,
    getRider,
    getActiveRideVehicle,
    getRideHistoryVehicle,
    getLicensePlatesBulk,
    decommissionVehicles,
    getVehicleDecommissionedInfo,
    undoDecommissionVehicles,
    soundAlarm as soundAlarmAPI,
    lockVehicle as lockVehicleAPI,
    openBatteryHatch as openBatteryHatchAPI,
    unlockVehicle as unlockVehicleAPI,
    rebootCCU as rebootCCUAPI,
    rebootIOT as rebootIOTAPI,
    updateVersion as updateVersionAPI,
    takeOwnership as takeOwnershipAPI,
    setVehicleStatus as setVehicleStatusAPI,
    giveUpOwnership as giveUpOwnershipAPI,
    replaceIot as replaceIotAPI,
} from 'src/api'
import {
    setCurrentVehicle,
    setRideHistory,
    setVehicleTaskHistory,
    setCurrentVehicleRider,
    setEndOfTaskHistoryPagination,
    incrementTaskHistoryPagination,
    getVehicle as getVehicleAction,
    getVehicleError,
    getVehicleLicensePlateSuccess,
    selectCurrentScooter,
    selectScooterRideHistory,
    selectScooterRideHistoryDateFilter,
    selectVehicleTaskHistory,
    selectVehicleTaskHistoryEndOfPagination,
    selectVehicleTaskHistoryPageNum,
    type GetVehicleAction,
    setVehicleDecommissionedInfo,
    setIsFetchingVehicleTaskHistory,
    setVehicleRideHistoryPagination,
} from 'src/redux/scooter'
import { appendTaskIdsWithParkingPhoto, setIsFetchingMoreTasksDone } from 'src/redux/hunterParkingPhotos'
import {
    UPDATE_VEHICLE_INFO,
    REMOVE_VEHICLE_FROM_REPAIR,
    GET_VEHICLE_TASK_HISTORY,
    GET_VEHICLE_TASK_HISTORY_NEXT_PAGE,
    INCREMENT_TASK_HISTORY_PAGINATION,
    GET_VEHICLE_RIDE_HISTORY,
    GET_VEHICLE,
    BIKE_UNARM_LOCK,
    DECOMMISSION_VEHICLE,
    GET_VEHICLE_DECOMMISSIONED_INFO,
    UNDO_DECOMMISSION_VEHICLE,
    SOUND_ALARM,
    LOCK_VEHICLE,
    OPEN_HATCH,
    UNLOCK_VEHICLE,
    REBOOT_CCU,
    REBOOT_IOT,
    UPDATE_VERSION,
    TAKE_OWNERSHIP,
    SET_STATUS,
    GIVE_UP_OWNERSHIP,
    REPLACE_IOT,
} from 'src/redux/scooter/scooter.types'
import { showNotification } from 'src/components/parts/notifications/notifications'
import { type LicensePlate } from 'src/models/licensePlate'
import {
    type DecommissionVehicleResult,
    type UndoDecommissionVehicleResult,
} from 'src/api/fm/decommission/decommission.model'

type GetVehicleRes = SagaReturnType<typeof getVehicle>
type GetActiveRideRes = SagaReturnType<typeof getActiveRideVehicle>
type GetActiveRiderRes = SagaReturnType<typeof getRider>

// Call this on page mount, gets vehicle by id/short/vin/imei
// the setCurrentVehicle to the response
export function* getVehicleData(action: GetVehicleAction<typeof GET_VEHICLE>) {
    const id = action.payload

    try {
        const vehicleRes: GetVehicleRes = yield call(getVehicle, id)

        if (vehicleRes instanceof Error) {
            throw vehicleRes
        }

        const activeRide: GetActiveRideRes = yield call(getActiveRideVehicle, vehicleRes.id)
        if (activeRide) {
            const ride = activeRide.data.data
            const activeRider: GetActiveRiderRes = yield call(getRider, ride.user_id)
            if (!(activeRider instanceof Error)) {
                const { userInfo } = activeRider
                yield put(
                    setCurrentVehicleRider({
                        id: userInfo.id,
                        email: userInfo.email,
                    }),
                )
            } else {
                yield put(setCurrentVehicleRider(null))
            }
        } else {
            yield put(setCurrentVehicleRider(null))
        }
        const licensePlates: LicensePlate[] | Error = yield call(getLicensePlatesBulk, [vehicleRes.id])
        const licensePlateNumber = licensePlates instanceof Error ? null : licensePlates[0]?.number
        yield put(setCurrentVehicle(vehicleRes))
        yield put(getVehicleLicensePlateSuccess(licensePlateNumber))
    } catch (_) {
        yield put(getVehicleError(`Your search "${id}" did not match any vehicles`))
    }
}

type GetVehicleDecommissionedInfoRes = SagaReturnType<typeof getVehicleDecommissionedInfo>

export function* getVehicleDecommissionedInfoSaga(action: GetVehicleAction<typeof GET_VEHICLE_DECOMMISSIONED_INFO>) {
    try {
        const { vehicleId } = action.payload
        const decommissionedInfo: GetVehicleDecommissionedInfoRes = yield call(getVehicleDecommissionedInfo, vehicleId)

        if (decommissionedInfo instanceof Error) {
            yield call(showNotification, 'error', 'Failed to get vehicle decommissioned info')
            return
        }

        yield put(setVehicleDecommissionedInfo(decommissionedInfo))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type GetRideHistoryRes = SagaReturnType<typeof getRideHistoryVehicle>

export function* fetchRideHistory({ payload: cursor }: GetVehicleAction<typeof GET_VEHICLE_RIDE_HISTORY>) {
    try {
        const scooter: ReturnType<typeof selectCurrentScooter> = yield select(selectCurrentScooter)
        if (!scooter) {
            throw new Error('Could not fetch vehicle')
        }
        const date: string | undefined = yield select(selectScooterRideHistoryDateFilter)

        const res: GetRideHistoryRes = yield call(getRideHistoryVehicle, scooter.id, { date, cursor })
        if (res instanceof Error) {
            throw res
        }

        let updatedHistory
        if (cursor !== '') {
            const currentHistory: ReturnType<typeof selectScooterRideHistory> = yield select(selectScooterRideHistory)
            updatedHistory = currentHistory ? currentHistory.concat(res.rentals) : res.rentals
        } else {
            updatedHistory = res.rentals
        }
        updatedHistory.sort((a, b) => b.startTime - a.startTime)
        yield put(setVehicleRideHistoryPagination(res.hasMore, res.nextCursor))
        yield put(setRideHistory(updatedHistory))
    } catch (e) {
        console.log('Failed to get ride history', e)
    }
}

type GetVehicleTaskHistoryRes = SagaReturnType<typeof getScooterHistoryTasks>

export function* fetchTaskHistory() {
    try {
        const page: ReturnType<typeof selectVehicleTaskHistoryPageNum> = yield select(selectVehicleTaskHistoryPageNum)
        const scooter: ReturnType<typeof selectCurrentScooter> = yield select(selectCurrentScooter)
        if (!scooter) {
            throw new Error('Could not fetch vehicle')
        }

        yield put(setIsFetchingVehicleTaskHistory(true))

        const data: GetVehicleTaskHistoryRes = yield call(getScooterHistoryTasks, scooter.id, page, 10, '-created_at')
        if (data instanceof Error) {
            throw data
        }

        if (!data || !data.length) {
            yield put(setEndOfTaskHistoryPagination())
        } else {
            const currentHistory: ReturnType<typeof selectVehicleTaskHistory> = yield select(selectVehicleTaskHistory)
            const updatedHistory = currentHistory.concat(data ? data : [])
            const newTaskIdsWithPhoto = data.filter(t => t.hasPhoto).map(t => t.id)
            yield put(setVehicleTaskHistory(updatedHistory))
            yield put(appendTaskIdsWithParkingPhoto(newTaskIdsWithPhoto, 'vehicle'))
        }
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    } finally {
        yield put(setIsFetchingMoreTasksDone())
        yield put(setIsFetchingVehicleTaskHistory(false))
    }
}

function* getTaskHistoryNextPage() {
    const endOfPagination: ReturnType<typeof selectVehicleTaskHistoryEndOfPagination> = yield select(
        selectVehicleTaskHistoryEndOfPagination,
    )
    if (!endOfPagination) {
        yield put(incrementTaskHistoryPagination())
    }
}

type UpdateVehicleRes = SagaReturnType<typeof updateVehicle>

export function* update(action: GetVehicleAction<typeof UPDATE_VEHICLE_INFO>) {
    try {
        const { vehicleId, data } = action.payload
        const res: UpdateVehicleRes = yield call(updateVehicle, vehicleId, data)
        if (res instanceof Error) {
            throw res
        }

        yield put(setCurrentVehicle(res))
        yield call(showNotification, 'success', 'Updated vehicle')
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type RemoveFromRepairRes = SagaReturnType<typeof removeFromRepairRequest>

export function* removeFromRepairs(action: GetVehicleAction<typeof REMOVE_VEHICLE_FROM_REPAIR>) {
    try {
        const { scooter } = action.payload
        const res: RemoveFromRepairRes = yield call(removeFromRepairRequest, [scooter.id])
        if (res instanceof Error) {
            throw res
        }

        if (!res.data.errors) {
            yield put(getVehicleAction(scooter.short))
            yield call(showNotification, 'success', 'Successfully removed scooter from repairs')
        } else {
            yield call(showNotification, 'error', 'Failed to remove scooter from repairs')
        }
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type BikeUnarmLockRes = SagaReturnType<typeof bikeUnarmLockRequest>

export function* bikeUnarmLock(action: GetVehicleAction<typeof BIKE_UNARM_LOCK>) {
    const bikeId = action.payload
    const res: BikeUnarmLockRes = yield call(bikeUnarmLockRequest, bikeId)

    if (res instanceof Error) {
        yield call(showNotification, 'error', res.message)
    } else {
        yield call(showNotification, 'success', 'Unarmed bike lock')
    }
}

export function* decommissionVehicleSaga(action: GetVehicleAction<typeof DECOMMISSION_VEHICLE>) {
    try {
        const { vehicleId, reason } = action.payload
        const decommissionVehiclesResults: DecommissionVehicleResult[] = yield call(
            decommissionVehicles,
            [vehicleId],
            reason,
        )

        if (decommissionVehiclesResults instanceof Error) {
            yield call(showNotification, 'error', 'Decommission failed')
            return
        }

        if (decommissionVehiclesResults[0].isSuccessful) {
            yield put(getVehicleAction(vehicleId))
            yield call(showNotification, 'success', 'Decommission succeeded')
        } else {
            yield call(showNotification, 'error', 'Decommission failed')
        }
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* undoDecommissionVehicleSaga(action: GetVehicleAction<typeof UNDO_DECOMMISSION_VEHICLE>) {
    try {
        const { vehicleId } = action.payload
        const undoDecommissionVehiclesResults: UndoDecommissionVehicleResult[] = yield call(undoDecommissionVehicles, [
            vehicleId,
        ])

        if (undoDecommissionVehiclesResults instanceof Error) {
            yield call(showNotification, 'error', 'Undo decommission failed')
            return
        }

        if (undoDecommissionVehiclesResults[0].isSuccessful) {
            yield put(getVehicleAction(vehicleId))
            yield call(showNotification, 'success', 'Undo decommission succeeded')
        } else {
            yield call(showNotification, 'error', 'Undo decommission failed')
        }
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* soundVehicleAlarm(action: GetVehicleAction<typeof SOUND_ALARM>) {
    try {
        const { vehicleId } = action.payload
        yield call(soundAlarmAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* lockVehicleFunc(action: GetVehicleAction<typeof LOCK_VEHICLE>) {
    try {
        const { vehicleId } = action.payload
        yield call(lockVehicleAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* openBatteryHatch(action: GetVehicleAction<typeof OPEN_HATCH>) {
    try {
        const { vehicleId } = action.payload
        yield call(openBatteryHatchAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* unlockVehicle(action: GetVehicleAction<typeof UNLOCK_VEHICLE>) {
    try {
        const { vehicleId } = action.payload
        yield call(unlockVehicleAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* rebootCCU(action: GetVehicleAction<typeof REBOOT_CCU>) {
    try {
        const { vehicleId } = action.payload
        yield call(rebootCCUAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* rebootIOT(action: GetVehicleAction<typeof REBOOT_IOT>) {
    try {
        const { vehicleId } = action.payload
        yield call(rebootIOTAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* updateVersion(action: GetVehicleAction<typeof UPDATE_VERSION>) {
    try {
        const { vehicleId } = action.payload
        yield call(updateVersionAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* takeOwnership(action: GetVehicleAction<typeof TAKE_OWNERSHIP>) {
    try {
        const { vehicleId } = action.payload
        yield call(takeOwnershipAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* setStatus(action: GetVehicleAction<typeof SET_STATUS>) {
    try {
        const { vehicleId, status } = action.payload
        yield call(setVehicleStatusAPI, vehicleId, status)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* giveUpOwnershipSaga(action: GetVehicleAction<typeof GIVE_UP_OWNERSHIP>) {
    try {
        const { vehicleId } = action.payload
        yield call(giveUpOwnershipAPI, vehicleId)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* replaceIotSaga(action: GetVehicleAction<typeof REPLACE_IOT>) {
    try {
        const { vehicleId, imei } = action.payload
        yield call(replaceIotAPI, vehicleId, imei)
        yield put(getVehicleAction(vehicleId))
        yield call(showNotification, 'success', 'IoT module replaced successfully')
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export default function* watcher() {
    yield takeEvery(GET_VEHICLE, getVehicleData)
    yield takeEvery(GET_VEHICLE_RIDE_HISTORY, fetchRideHistory)
    yield takeEvery(GET_VEHICLE_TASK_HISTORY, fetchTaskHistory)
    yield takeEvery(GET_VEHICLE_DECOMMISSIONED_INFO, getVehicleDecommissionedInfoSaga)
    yield takeEvery(INCREMENT_TASK_HISTORY_PAGINATION, fetchTaskHistory)
    yield takeEvery(GET_VEHICLE_TASK_HISTORY_NEXT_PAGE, getTaskHistoryNextPage)
    yield takeEvery(UPDATE_VEHICLE_INFO, update)
    yield takeEvery(REMOVE_VEHICLE_FROM_REPAIR, removeFromRepairs)
    yield takeEvery(BIKE_UNARM_LOCK, bikeUnarmLock)
    yield takeEvery(DECOMMISSION_VEHICLE, decommissionVehicleSaga)
    yield takeEvery(UNDO_DECOMMISSION_VEHICLE, undoDecommissionVehicleSaga)
    yield takeEvery(SOUND_ALARM, soundVehicleAlarm)
    yield takeEvery(LOCK_VEHICLE, lockVehicleFunc)
    yield takeEvery(OPEN_HATCH, openBatteryHatch)
    yield takeEvery(UNLOCK_VEHICLE, unlockVehicle)
    yield takeEvery(REBOOT_CCU, rebootCCU)
    yield takeEvery(REBOOT_IOT, rebootIOT)
    yield takeEvery(UPDATE_VERSION, updateVersion)
    yield takeEvery(TAKE_OWNERSHIP, takeOwnership)
    yield takeEvery(SET_STATUS, setStatus)
    yield takeEvery(GIVE_UP_OWNERSHIP, giveUpOwnershipSaga)
    yield takeEvery(REPLACE_IOT, replaceIotSaga)
}
